import {
  type BaseChainflipAsset,
  baseChainflipAssets,
  type ChainflipAsset,
  chainflipAssetToPriceAssetMap,
  type AnyChainflipAsset,
  chainflipAssets,
} from '@chainflip/utils/chainflip';
import { CHAINFLIP_SS58_PREFIX } from '@chainflip/utils/consts';
import * as ss58 from '@chainflip/utils/ss58';
import * as Sentry from '@sentry/react';
import BigNumber from 'bignumber.js';
import type TokenAmount from '@/shared/utils/TokenAmount';

type BlockExplorerEntity = 'tx' | 'address' | 'block';

export const etherscanUrl = (type: BlockExplorerEntity, id: string | number): string => {
  let subdomain = '';
  if (Number(process.env.NEXT_PUBLIC_ETHEREUM_NETWORK_ID) === 11155111) {
    subdomain = 'sepolia.';
  }
  const baseUrl = `https://${subdomain}etherscan.io`;

  return new URL(`/${type}/${id}`, baseUrl).toString();
};

export const arbiscanUrl = (type: BlockExplorerEntity, id: string | number): string => {
  let subdomain = '';
  if (Number(process.env.NEXT_PUBLIC_ARBITRUM_NETWORK_ID) === 421614) {
    subdomain = 'sepolia.';
  }
  const baseUrl = `https://${subdomain}arbiscan.io`;
  return new URL(`/${type}/${id}`, baseUrl).toString();
};

export const solscanUrl = (type: BlockExplorerEntity, id: string | number): string => {
  const urlPath = type === 'address' ? `/account/${id}` : `/${type}/${id}`;

  switch (process.env.NEXT_PUBLIC_CHAINFLIP_NETWORK) {
    case 'mainnet':
      return new URL(urlPath, 'https://solscan.io').toString();
    default:
      return new URL(`${urlPath}?cluster=devnet`, 'https://explorer.solana.com').toString();
  }
};

export const tronscanUrl = (type: BlockExplorerEntity, id: string | number): string => {
  const urlPath =
    type === 'address'
      ? `#/contract/${id}/transactions`
      : `#/${type === 'tx' ? 'transaction' : type}/${id}`;

  switch (process.env.NEXT_PUBLIC_CHAINFLIP_NETWORK) {
    case 'mainnet':
      return new URL(urlPath, 'https://tronscan.org').toString();
    default:
      return new URL(urlPath, 'https://nile.tronscan.org').toString();
  }
};

// BSC's chain ids are fixed per network (56 / 97), so this switches on the chainflip network
// instead of a NEXT_PUBLIC_BSC_NETWORK_ID env var, following solscanUrl/tronscanUrl.
export const bscscanUrl = (type: BlockExplorerEntity, id: string | number): string => {
  const urlPath = `/${type}/${id}`;

  switch (process.env.NEXT_PUBLIC_CHAINFLIP_NETWORK) {
    case 'mainnet':
      return new URL(urlPath, 'https://bscscan.com').toString();
    default:
      return new URL(urlPath, 'https://testnet.bscscan.com').toString();
  }
};

export const buildUrl = (path: string, base: string | undefined): string => {
  if (!base) return path;
  try {
    return new URL(path, base).toString();
  } catch {
    return path;
  }
};

type ComparatorArg = string | number | BigNumber | bigint;

export type Comparator = Record<'ASC' | 'DESC', (a: ComparatorArg, b: ComparatorArg) => 1 | 0 | -1>;

const compareAscending = (a: ComparatorArg, b: ComparatorArg): 1 | 0 | -1 => {
  if (BigNumber.isBigNumber(a) && BigNumber.isBigNumber(b)) {
    return a.comparedTo(b) ?? 0;
  }
  if (a < b) return -1;
  if (a === b) return 0;
  return 1;
};

export const COMPARATORS: Comparator = {
  ASC: compareAscending,
  DESC: (a: ComparatorArg, b: ComparatorArg) => compareAscending(b, a),
};

export const copy = (text: string): Promise<boolean> =>
  navigator.clipboard
    .writeText(text)
    .then(() => true)
    .catch(() => false);

export const isChainflipSs58Address = (text: string): text is `cF${string}` => {
  try {
    return ss58.decode(text).ss58Format === CHAINFLIP_SS58_PREFIX;
  } catch (err) {
    return false;
  }
};

// Rule that tries to find a match between any of the event stack frames and the provided list upon creation
const hasMatchingStackframesFactory: (
  stackframes: (string | RegExp)[],
) => (error: Sentry.ErrorEvent) => boolean = (stackframes) => (event) => {
  // if the event has an exception
  if (event.exception?.values && event.exception.values.length > 0) {
    for (const exception of event.exception.values) {
      const stacktraceFrames = exception.stacktrace?.frames;

      // and it has stacktrace frames
      if (stacktraceFrames && stacktraceFrames.length > 0) {
        // check if any of the rule-provided stack frames match the stack frame names
        const framesMatch = stacktraceFrames.some((frame) =>
          stackframes.some((source) => frame.filename?.match(source)),
        );

        if (framesMatch) {
          return true;
        }
      }
    }
  }
  return false;
};

export const initSentry = () => {
  if (!process.env.NEXT_PUBLIC_SENTRY_DSN) return;

  const hasMatchingStackframesCheck = hasMatchingStackframesFactory([
    /walletconnect/gi,
    /xdefi/gi,
    /wagmi/gi,
    /viem/gi,
    /coinbase/gi,
    /moz-extension:\/\//gi,
    /chrome-extension:\/\//gi,
    /sentry-internal/gi,
  ]);

  try {
    Sentry.init({
      dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.extraErrorDataIntegration({ depth: 5 }),
        Sentry.sessionTimingIntegration(),
      ],
      tracesSampleRate: Number(process.env.NEXT_PUBLIC_SENTRY_SAMPLE_RATE) || undefined,
      allowUrls: [/chainflip\.io/],
      release: process.env.NEXT_PUBLIC_RELEASE_VERSION,
      tunnel: '/sentry',
      ignoreErrors: [
        // network errors
        /(network|fetch|axios) ?error/i,
        /failed to fetch/i,
        /load failed/i, // safari
        /networkerror when attempting to fetch resource/i, // firefox
        /network request failed/i,

        // browser/environment noise
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        /script error\.?$/i,
        'Non-Error exception captured',
        'Non-Error promise rejection captured',
        /loading chunk \d+ failed/i,
        /loading css chunk \d+ failed/i,
        'ChunkLoadError',
        /dynamically imported module/i,
        "can't access dead object", // firefox extension cleanup
        /Failed to execute 'removeChild' on 'Node'/,
        /Failed to execute 'insertBefore' on 'Node'/,
        /Object Not Found Matching Id/i,

        // wallet/web3 user actions
        /user rejected/i,
        /user denied/i,
        /user cancelled/i,
        /rejected the request/i,
        /request rejected/i,
        /user canceled/i,

        // third-party wallet noise
        /xdefi/i,
        /walletconnect/i,
      ],
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
      ],
      // Returning null in case of a rule match will prevent the event from being sent
      beforeSend: (event) => (hasMatchingStackframesCheck(event) ? null : event),
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('failed to initialize sentry:', error);
  }
};

export const noop = () => {
  // pass
};

// for tests only, do not use in production
/* eslint-disable @typescript-eslint/no-explicit-any */
export const deepReplace = (obj: any, replacements: Record<string, (value: any) => any>): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => deepReplace(item, replacements)) as any;
  }

  if (typeof obj === 'object' && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        const replacer = replacements[key];

        return [key, replacer ? replacer(value) : deepReplace(value, replacements)];
      }),
    );
  }

  return obj;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export function getAmountFromRate(amount: TokenAmount, rate: undefined): undefined;
export function getAmountFromRate(amount: TokenAmount, rate: number): TokenAmount;
export function getAmountFromRate(
  amount: TokenAmount,
  rate: number | undefined,
): TokenAmount | undefined {
  return rate !== undefined ? amount.mul(rate) : undefined;
}

export function parseAsset(
  param: string | string[] | undefined,
  opts?: { default: AnyChainflipAsset },
): ChainflipAsset;
export function parseAsset(
  param: string | string[] | undefined,
  opts: { type: 'base'; default?: BaseChainflipAsset },
): BaseChainflipAsset;
export function parseAsset(
  param: string | string[] | undefined,
  opts: { type: 'all'; default?: AnyChainflipAsset },
): ChainflipAsset;
export function parseAsset(
  param: string | string[] | undefined,
  {
    type = 'all',
    default: defaultAsset = 'Eth',
  }: { type?: 'all' | 'base'; default?: AnyChainflipAsset } = {},
) {
  if (typeof param !== 'string') return defaultAsset;
  const lower = param.toLowerCase();
  return (
    (type === 'all' ? chainflipAssets : baseChainflipAssets).find(
      (a) => a.toLowerCase() === lower,
    ) ?? defaultAsset
  );
}

export const parsePricedAsset = (
  param: string | string[] | undefined,
  defaultAsset: ChainflipAsset = 'Usdc',
): ChainflipAsset => {
  if (typeof param !== 'string') return defaultAsset;
  const lower = param.toLowerCase();
  const asset = chainflipAssets.find(
    (a) => a.toLowerCase() === lower && chainflipAssetToPriceAssetMap[a] !== null,
  );

  return asset ?? defaultAsset;
};
